import { useTranslation } from '@fluentsms/i18n';
import { createStyles, makeStyles, TableBody, TableCell, TableRow, TableRowProps } from '@material-ui/core';
import clsx from 'clsx';
import React, { FC, ReactNode } from 'react';
import { ColumnInstance, HeaderGroup, Row } from 'react-table';
import { DataGridLoadingRows } from '../DataGridLoadingRows/DataGridLoadingRows';
import DataGridNoDataBody, { DataGridNoDataBodyProps } from '../DataGridNoDataBody';
import { VirtualizeOptions } from './DataGrid';
import { VirtualizedTableBody } from './VirtualizedTableBody';

// this type is needed for prop types validation
export type CustomTableBodyProps = {
  children: ReactNode[];
  component: string;
};

export type TableBodyComponentProps<TBodyProps extends Record<string, unknown> = Record<string, unknown>> = TBodyProps;

export type CustomTableRowProps = TableRowProps & {
  children: ReactNode;
};

export type TableRowComponentProps = {
  component: string;
};

export type TableBodyComponentContainerProps<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
> = {
  loading?: boolean;
  allColumns: ColumnInstance<TRowData>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableOptions?: { [key: string]: any };
  headerGroups: HeaderGroup<TRowData>[];
  displayNoColumnMessage: boolean;
  rows: Row<TRowData>[];
  virtualize?: boolean;
  data?: TRowData[];
  onRowClick?: (row: Row<TRowData>) => void;
  prepareRow: (row: Row<TRowData>) => void;
  tableStateChangeListener: boolean;
  virtualizeOptions?: VirtualizeOptions;
  onScroll?: (args: {
    scrollDirection: 'forward' | 'backward';
    scrollOffset: number;
    scrollUpdateWasRequested: boolean;
  }) => void;
  TableBodyComponent?: (tableBodyProps: CustomTableBodyProps & TableBodyComponentProps<TBodyProps>) => JSX.Element;
  TableBodyComponentProps?: TableBodyComponentProps<TBodyProps>;
  TableRowComponent?: ({ children }: CustomTableRowProps) => JSX.Element;
  TableRowComponentProps?: TableRowComponentProps;
  NoDataComponent?: FC<DataGridNoDataBodyProps>;
  NoDataComponentProps?: DataGridNoDataBodyProps;
  hasActiveFilters?: boolean;
};

const useStyles = makeStyles(() =>
  createStyles({
    cell: {
      display: 'flex',
    },
    nonResizeable: {
      display: 'flex',
      flex: '1 0 auto !important',
    },
  }),
);

export function TableBodyComponentContainer<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
>({
  loading,
  allColumns,
  tableOptions,
  headerGroups,
  displayNoColumnMessage,
  rows,
  virtualize = true,
  data = [],
  onRowClick = () => undefined,
  prepareRow,
  tableStateChangeListener,
  virtualizeOptions,
  onScroll,
  TableBodyComponentProps,
  TableBodyComponent = TableBody,
  TableRowComponent = TableRow,
  TableRowComponentProps = { component: 'div' },
  NoDataComponent = DataGridNoDataBody,
  NoDataComponentProps = {},
  hasActiveFilters,
}: TableBodyComponentContainerProps<TRowData, TBodyProps>) {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const displayMessageProps = {
    ...(!loading && displayNoColumnMessage
      ? {
          headerMessage: t('dataGrid.nothingToDisplay'),
          actionMessage: t('dataGrid.allColumnsOff'),
        }
      : !loading && !rows.length && hasActiveFilters
      ? {
          headerMessage: t('dataGrid.noResults'),
          actionMessage: t('dataGrid.tryDifferentSearch'),
        }
      : !loading && !rows.length
      ? {
          headerMessage: t('dataGrid.noResults'),
          actionMessage: t('dataGrid.tryDifferentSearch'),
          ...NoDataComponentProps,
        }
      : {}),
  };

  return (
    <TableBodyComponent component="div" data-testid="table-body-component" {...(TableBodyComponentProps as any)}>
      {loading && (
        <DataGridLoadingRows
          allColumns={(allColumns as unknown) as ColumnInstance[]}
          hiddenColumns={tableOptions?.initialState?.hiddenColumns}
          headerGroups={(headerGroups as unknown) as HeaderGroup[]}
          tableOptions={tableOptions}
        />
      )}

      {Object.entries(displayMessageProps)?.length ? (
        <TableRow component="div">
          <TableCell component="div">
            <NoDataComponent {...displayMessageProps} />
          </TableCell>
        </TableRow>
      ) : /** Determine whether or not we render a virtualized body or regular body */
      !loading && virtualize ? (
        <VirtualizedTableBody<TRowData>
          data={data}
          rows={rows}
          onRowClick={onRowClick}
          tableOptions={tableOptions}
          prepareRow={prepareRow}
          tableStateChangeListener={tableStateChangeListener}
          virtualizeOptions={virtualizeOptions}
          onScroll={onScroll}
        />
      ) : (
        !loading &&
        rows.map((row, rowIndex) => {
          prepareRow(row);
          return (
            <TableRowComponent
              {...TableRowComponentProps}
              hover
              {...row.getRowProps()}
              selected={row.isSelected}
              key={rowIndex}
            >
              {row.cells.map((cell, cellIndex) => {
                const isMenuColumn = cell.column.Header === 'Menu';

                return (
                  <TableCell
                    {...cell.getCellProps()}
                    component="div"
                    onClick={() => onRowClick(row)}
                    className={clsx(classes.cell, tableOptions?.disableResizing == true && classes.nonResizeable)}
                    key={`row-${rowIndex}-cell-${cellIndex}`}
                    data-testid="data-grid-cell"
                  >
                    {isMenuColumn
                      ? cell.render('Cell', {
                          row,
                        })
                      : cell.render('Cell')}
                  </TableCell>
                );
              })}
            </TableRowComponent>
          );
        })
      )}
    </TableBodyComponent>
  );
}
